import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { IProfileData } from '../../interfaces/IAuth';
import { IApiClaimsHistory, ICancelBalance, ICancellationReason, IContract, IContractData, IFfsItem, ILinkPlanForUI, IOffer, IPaymentInfo, IPrimaryCustomerPhone, ITandC } from '../../interfaces/IJob';
import { IMessage } from '../messages/messages.interface';
import { ActivatedRoute } from '@angular/router';
import { ClientIdForLogos, SiteParamsValues, Site, LogoClassNames, PAST_DUE_BANNER_MESSAGE, PAYMENT_PENDING_BANNER_MESSAGE, CALL_FOR_UPDATE_PHONE_NUMBER, ConfirmationType, FIRST_TIME_USER, PAGE_SOURCE, REMAX_LOGO_ID } from '../../utils/constants';
import { IPaymentBannerItem } from '../../interfaces/IBanner';
import { IPaymentFlow } from '../../interfaces/IPayments';
import { IAvailabilityResponse, IItems, IItemsAndLocationsResponse, IOneTimeCourtesy, ISelectedProduct, IDeductibleResponse, IClaimResponse, IJobByModelResponse, IPMItem, IRecallServiceItem, IClaimReimbursementResponse, ICategoryItems, ICoveredCategory, IExtractBarcodeResponseData } from '../../components/pages/service-request/service-request.interface';
import { UtilitiesService } from '../utilities/utilities.service';
import { IPhoneListDisplay, IProfile } from '../../components/pages/service-request-history/service-request-history.interface';
import { IAccessTokenDetails, IClaimDetailsServiceJob, IJobStatusResponse, IServiceJob } from '../../components/pages/claim-history/claim-history.interface';
import { FFS_ENABLED, RE_RENEWAL_BANNER_ENABLED, REMAX_LOGO_ENABLED } from '../../utils/feature-flags';
import { CONTRACT_STATUS_UPPER_ACTIVE, HMS_CLIENT, PURCHASE_TYPE_CODE_SHW } from '../../components/pages/my-plan/my-plan.constants';
import { IAlertCardContent } from '../../interfaces/IAlertCard';
import { environment } from '../../../environments/environment';
import { IFeatureFlagsPerEnvironment } from '../../interfaces/IFeatureFlags';
import { ItemSelectionWay } from '../ga4/ga4.interface';
import { IBaseCoverageResponse, IContractEnrollmentResponse } from '../../interfaces/IEnrollment';
import { ICrossSellContent } from '../../interfaces/ICrossSellContent';
import { IContactUsPrefill } from '../../interfaces/IContactUs';
import { normalizeContractAddresses } from '../../utils/helper';

class NormalizedContractSubject extends BehaviorSubject<IContractData | null> {
  override next(value: IContractData | null): void {
    super.next(normalizeContractAddresses(value) ?? null);
  }
}

@Injectable({
  providedIn: 'root',
})
export class DataService {
  user$: BehaviorSubject<any> = new BehaviorSubject<any>(null); // is this IUserData interface that is not used?
  hideNavigation$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  contactInfoConfirmed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  sub?: string;
  auth0UserId: BehaviorSubject<string> = new BehaviorSubject<string>('');
  profileData$: BehaviorSubject<IProfileData | null> =
    new BehaviorSubject<IProfileData | null>(null);
  contractData$: BehaviorSubject<IContractData | null> =
    new NormalizedContractSubject(null);
  currentContractNumber$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  cancelBalanceData$: BehaviorSubject<ICancelBalance | null> = new BehaviorSubject<ICancelBalance | null>(null);
  jobData$: BehaviorSubject<IApiClaimsHistory[] | null> = new BehaviorSubject<IApiClaimsHistory[] | null>([]);
  // true when the latest JobService.getJobsData call for the current contract failed
  jobDataFailed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  recentJobData$: BehaviorSubject<IApiClaimsHistory[] | null> = new BehaviorSubject<IApiClaimsHistory[] | null>([]);
  tncPdf$: BehaviorSubject<ITandC | null> = new BehaviorSubject<ITandC | null>(
    null
  );
  loginAttempted$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  resetEmailSent$: BehaviorSubject<{ error: boolean; message: string } | null> = new BehaviorSubject<{ error: boolean; message: string } | null>(null);
  messagesToDisplay$: BehaviorSubject<IMessage[]> = new BehaviorSubject<IMessage[]>([]);
  siteName$ = new BehaviorSubject<string>('');
  LogoClass$ = new BehaviorSubject<string>(LogoClassNames.CINCH_DEFAULT);
  firstTimeUser$ = new BehaviorSubject<boolean>(false);
  unlinkedContract$: BehaviorSubject<IContractData | null> =
    new BehaviorSubject<IContractData | null>(null);
  lastServiceRequestContractNumber: string | null = null;

  cancellationReasons$ = new BehaviorSubject<ICancellationReason[] | null>(null);
  selectedCancellationReason$ = new BehaviorSubject<ICancellationReason | null>(null);
  selectedCancelOption$ = new BehaviorSubject<string>('');
  offers$: BehaviorSubject<IOffer[] | null> = new BehaviorSubject<IOffer[] | null>(null);
  isPlanLinked$: BehaviorSubject<ILinkPlanForUI> = new BehaviorSubject<ILinkPlanForUI>({ linked: false, contractNumber: null });
  isFromPayment: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  alreadyNonAutoRenewed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  removeToast$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  paymentInitiation$: BehaviorSubject<IPaymentFlow | null> = new BehaviorSubject<IPaymentFlow | null>(null);

  serviceRequestSelectedCategoryValue$ = new BehaviorSubject<{ label: string, value: string } | null>(null);
  serviceRequestEligibleCategories$ = new BehaviorSubject<number[]>([]);
  serviceRequestEligibleItems$ = new BehaviorSubject<number[]>([]);
  serReqSubCategoryLocations$ = new BehaviorSubject<IItemsAndLocationsResponse | null>(null);
  serviceRequestSelectedItem$ = new BehaviorSubject<IItems | null>(null);
  serviceRequestSelectedProducts$ = new BehaviorSubject<ISelectedProduct[]>([])
  isWeatherBannerVisible$ = new BehaviorSubject<boolean>(true);
  isAddItemClicked$ = new BehaviorSubject<boolean>(false);
  serviceRequestAvailablity$ = new BehaviorSubject<IAvailabilityResponse | null>(null);
  claimCreditInfoUpdateStatus$: Subject<boolean> = new Subject<boolean>();

  totalDeductible$ = new BehaviorSubject<number | null>(null);
  deductibleResponse$ = new BehaviorSubject<IDeductibleResponse | null>(null);
  claimResponse$ = new BehaviorSubject<IClaimResponse | null>(null);
  confirmationType$ = new BehaviorSubject<ConfirmationType | null>(null);
  isRecall$ = new BehaviorSubject<boolean>(false);
  isDirectPM$ = new BehaviorSubject<boolean>(false);
  isPreventativeMaintenance$ = new BehaviorSubject<boolean>(false);
  isCancelRequested$ = new BehaviorSubject<boolean>(false);
  recallJob$ = new BehaviorSubject<IApiClaimsHistory | IServiceJob | null>(null);
  recallItem$ = new BehaviorSubject<IJobByModelResponse | null>(null);
  claimReimbursmentServiceJobIds$ = new BehaviorSubject<string[]>([]);
  setClaimReimbursementResponse$ = new BehaviorSubject<IClaimReimbursementResponse[]>([])
  recallDialogClose$ = new Subject<void>();
  isDifferentIssueNewJob$ = new BehaviorSubject<boolean>(false);
  isVivr$ = new BehaviorSubject<boolean>(false);
  vivrSub$ = new BehaviorSubject<string>('');
  vivrSource$ = new BehaviorSubject<string>('');
  vivrAccessTokenData$ = new BehaviorSubject<IAccessTokenDetails | null>(null);
  vivrAccessToken$ = new BehaviorSubject<string>('');
  jobStatus$ = new BehaviorSubject<IJobStatusResponse | null>(null);
  tunnelHeaderHeading$ = new BehaviorSubject<string>('');
  isPlanFFSSelected$ = new BehaviorSubject<IFfsItem | null>(null);
  itemMatched$ = new BehaviorSubject<IExtractBarcodeResponseData | null>(null);

  navigationSource$ = new BehaviorSubject<string | null>(null);
  itemAvailabilityResults$ = new BehaviorSubject<{ itemId: string, available: boolean }[] | null>(null);
  serviceRequestItemSelectionWay$ = new BehaviorSubject<ItemSelectionWay | null>(null);

  oneTimeCourtesy$ = new BehaviorSubject<IOneTimeCourtesy>({
    eligibleOneTime: false,
    isEligibleServiceRequest: true,
    isClosingDateMoreThan30: false
  });

  serviceRequestPMData$ = new BehaviorSubject<IPMItem[]>([]);
  serviceRequestDetailStatus$ = new BehaviorSubject<IJobStatusResponse | null>(null);
  contactUsPrefill$ = new BehaviorSubject<IContactUsPrefill | null>(null);
  globalLoader$ = new BehaviorSubject<boolean>(false);
  profileDashboardApiErrorState$ = new BehaviorSubject<boolean>(false);

  isLoginPage$ = new BehaviorSubject<boolean>(false);

  crossSellCEEligible$ = new BehaviorSubject<boolean>(false);
  // Emits when the My Plan CE card is displayed, i.e. its content and price have loaded.
  crossSellCEMyPlanCardDisplayed$ = new Subject<void>();
  crossSellSurgeEligible$ = new BehaviorSubject<boolean>(false);

  itemsList$ = new BehaviorSubject<any>(null);
  coveredItems$ = new BehaviorSubject<{ categoryIds: number[] } | null>(null);
  locationsList$ = new BehaviorSubject<any>(null);
  categoriesList$ = new BehaviorSubject<ICategoryItems[] | null>(null);
  brandsList$ = new BehaviorSubject<any>(null);
  coveredCategories$ = new BehaviorSubject<ICoveredCategory | null>(null);
  serviceRequestWithAutoComplete$ = new BehaviorSubject<boolean>(false);
  recentlyCancelledJob$ = new BehaviorSubject<{ serviceOrderId: string | null, isJobFFS: boolean } | null>(null);
  featureFlags$ = new BehaviorSubject<IFeatureFlagsPerEnvironment | null>(null);
  enteredEmail$ = new BehaviorSubject<string>('');

  sampleContractPdfUrl$ = new BehaviorSubject<string | null>(null);
  productInfo$ = new BehaviorSubject<IBaseCoverageResponse | null>(null);
  crossSellContent$ = new BehaviorSubject<ICrossSellContent | null>(null);
  enrollmentInfo$ = new BehaviorSubject<IContractEnrollmentResponse | null>(null);
  enrollmentPaymentInfo$ = new BehaviorSubject<IPaymentInfo | null>(null);
  consentError$ = new Subject<boolean>();
  consentNotChecked$ = new Subject<boolean>();

  constructor(private route: ActivatedRoute, private utilitiesService: UtilitiesService) {
    this.initializeSiteName();
    this.subscribeToQueryParams();
    this.subscribeToSiteName();
    this.updateLogoWithCurrentClientId();

    let lastContractNumber: string | null = null;
    this.contractData$.subscribe((contract) => {
      if (contract) {
        if (lastContractNumber !== contract.baseContract.contractNumber) {
          lastContractNumber = contract.baseContract.contractNumber;
          this.coveredCategories$.next(null);
        }
      } else {
        lastContractNumber = null;
        this.coveredCategories$.next(null);
      }
    });
  }


  private initializeSiteName(): void {
    const siteName = sessionStorage.getItem(Site) || '';
    this.siteName$.next(siteName);
  }

  private subscribeToQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params.hasOwnProperty(Site) && params[Site]) {
        this.siteName$.next(params[Site]);
      }
    });
  }

  private subscribeToSiteName(): void {
    this.siteName$.subscribe({
      next: (txt) => {
        this.updateLogo(txt);
        if (txt !== '') {
          sessionStorage.setItem(Site, txt);
        }
      }
    });
  }

  updateLogoWithCurrentClientId(): void {
    this.contractData$.subscribe({
      next: (contractData) => {
        const clientId = contractData?.baseContract?.clientId;
        const logoId = contractData?.baseContract?.logoId;
        if (REMAX_LOGO_ENABLED && logoId === REMAX_LOGO_ID) {
          this.updateLogo(SiteParamsValues.remax);
        } else if (clientId) {
          this.updateLogo(clientId);
        }
      }
    });
  }

  updateLogo(clientOrSiteValue: string): void {
    const logoMap: { [key: string]: string } = {
      [ClientIdForLogos.sears]: LogoClassNames.CINCH_SEARS,
      [SiteParamsValues.sears]: LogoClassNames.CINCH_SEARS,
      [ClientIdForLogos.ghs]: LogoClassNames.CINCH_GOODMAN,
      [ClientIdForLogos.ghp]: LogoClassNames.CINCH_GOODMAN,
      [SiteParamsValues.goodman]: LogoClassNames.CINCH_GOODMAN,
      [ClientIdForLogos.txu]: LogoClassNames.CINCH_TXU,
      [SiteParamsValues.txu]: LogoClassNames.CINCH_TXU,
      [SiteParamsValues.remax]: LogoClassNames.CINCH_REMAX
    };
    const logoClass = logoMap[clientOrSiteValue] || LogoClassNames.CINCH_DEFAULT;
    this.LogoClass$.next(logoClass);
  }

  // first time user session storage
  setFirstTimeUserInSession(): void {
    sessionStorage.setItem(FIRST_TIME_USER, 'true');
    this.firstTimeUser$.next(true);
  }

  // these 2 methods will be used
  // when we work on showing the what to expect carousal just for first time users
  getFirstTimeUserFromSession(): void {
    const firstTimeUserFromSession = sessionStorage.getItem(FIRST_TIME_USER);
    this.firstTimeUser$.next(firstTimeUserFromSession === 'true');
  }

  clearFirstTimeUserFromSession(): void {
    sessionStorage.removeItem(FIRST_TIME_USER);
  }

  getPaymentBannerItem(): IPaymentBannerItem {
    const contractData = this.contractData$.getValue();
    const paymentBannerItem: IPaymentBannerItem = {
      showBanner: false,
      message: '',
      severity: '',
      icon: '',
      value: ''
    };

    // no banner shown on hide pending payments
    if (!contractData || contractData?.userStatuses?.HIDE_PENDING_PAYMENTS) {
      return paymentBannerItem;
    }

    // payment pending
    if (contractData?.pendingPayments > 0 && !contractData.userStatuses?.CONTRACT_EXPIRED && !contractData.userStatuses?.CONTRACT_CANCELLED) {
      return {
        ...paymentBannerItem,
        showBanner: true,
        message: PAYMENT_PENDING_BANNER_MESSAGE,
        severity: 'info',
        icon: 'pi pi-check-circle',
        value: 'pendingPayment'
      }
    }

    // payment due
    if (contractData.userPermissions?.MAKE_PAYMENT && (!contractData.userStatuses?.CONTRACT_EXPIRED || !contractData.userStatuses?.CONTRACT_CANCELLED)) {
      if (!contractData.userStatuses?.OVERRIDE_PENDING_CANCEL) {
        return {
          ...paymentBannerItem,
          showBanner: true,
          message: PAST_DUE_BANNER_MESSAGE,
          severity: 'error',
          icon: 'pi pi-info-circle',
          value: 'pastDuePayment'
        }
      }

      if (contractData.userStatuses?.IS_FINANCIAL_CHANNEL && !contractData.userStatuses?.OVERRIDE_PENDING_CANCEL) {
        return {
          ...paymentBannerItem,
          showBanner: true,
          message: PAST_DUE_BANNER_MESSAGE,
          severity: 'error',
          icon: 'pi pi-info-circle',
          value: 'pastDuePayment'
        }
      }
    }

    return paymentBannerItem;
  }

  public checkOneTimeCourtesy(baseContract: IContract, claimsCount: number) {
    const hmsPurchaseTypeCode = ['BCW', 'BDW', 'NHW', 'ODW'];
    /*
    "BUYER_CONVERSION": "BCW",
    "BUYER_DIRECT": "BDW",
    "NEW_HOME": "NHW",
    "OPEN_DIRECT": "ODW",
    */
    let oneTimeCourtesy: IOneTimeCourtesy = {
      eligibleOneTime: false,
      isEligibleServiceRequest: true,
      isClosingDateMoreThan30: false
    }
    if (baseContract) {
      // Check if the contractYear is 0, clientId is HMS, and purchaseTypeCode matches the required values
      if (baseContract.contractYear === '0' &&
        baseContract.clientId === 'HMS' &&
        hmsPurchaseTypeCode.includes(baseContract.purchaseTypeCode) &&
        baseContract.paymentCount === 0
      ) {
        if (baseContract.purchaseTypeCode !== 'ODW' && baseContract.closingDate) {
          const currentDate = new Date();
          const closingYear = parseInt(baseContract.closingDate.toString().substring(0, 4), 10);
          const closingMonth = parseInt(baseContract.closingDate.toString().substring(4, 6), 10) - 1; // Months in JavaScript are zero-based
          const closingDay = parseInt(baseContract.closingDate.toString().substring(6), 10);
          const closingDate = new Date(closingYear, closingMonth, closingDay);

          const timeDiff = closingDate.getTime() - currentDate.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          if (Math.abs(daysDiff) <= 30) {
            return this.oneTimeCourtesy$.next(this.setOneTimeCourtesy(baseContract.paymentCount, claimsCount, oneTimeCourtesy));
          } else {
            return this.oneTimeCourtesy$.next(
              {
                eligibleOneTime: false,
                isEligibleServiceRequest: false,
                isClosingDateMoreThan30: true
              });
          }
        } else {
          return this.oneTimeCourtesy$.next(this.setOneTimeCourtesy(baseContract.paymentCount, claimsCount, oneTimeCourtesy));
        }
      }
    }
    this.oneTimeCourtesy$.next(oneTimeCourtesy);
  }

  private setOneTimeCourtesy(paymentCount: number, jobsCount: number, oneTimeCourtesy: IOneTimeCourtesy): IOneTimeCourtesy {
    if (paymentCount === 0 && jobsCount === 0) {
      oneTimeCourtesy = {
        ...oneTimeCourtesy,
        eligibleOneTime: true
      }
    } else if (paymentCount === 0 && jobsCount > 0) {
      oneTimeCourtesy = {
        ...oneTimeCourtesy,
        isEligibleServiceRequest: false
      }
    }
    return oneTimeCourtesy;
  }


  getProfileViewOnFormat(contract: IContractData): IProfile | null {
    if (!contract?.baseContract) {
      return null;
    }
    const out: IProfile = {
      propertyAddress: contract.baseContract.propertyAddress,
      callForUpdate: CALL_FOR_UPDATE_PHONE_NUMBER,
      mailingAddress: contract.mailingAddress,
      contactName: [contract.baseContract.primaryCustomer.firstName, contract.baseContract.primaryCustomer.lastName].filter(Boolean).join(' '),
      contactEmail: contract.baseContract.primaryCustomerEmail,
      contactPhone: this.getPhoneOnFormat(contract.baseContract.primaryCustomerPhone),
      password: '******',
      phoneList: this.getAllPhonesForDisplay(contract)
    };
    return out;
  }

  getAllPhonesForDisplay(contract: IContractData): IPhoneListDisplay[] {
    let out: IPhoneListDisplay[] = [];
    if (contract.contact?.phoneList) {
      contract.contact.phoneList.map((phone: IPrimaryCustomerPhone) => {
        if (phone.phoneNumber && phone.phoneNumber !== '') {
          out.unshift(
            {
              type: phone.phoneType || undefined,
              phone: this.getPhoneOnFormat(phone)
            }
          );
        }
      });
    }
    return out;
  }

  getPhoneOnFormat(phone: IPrimaryCustomerPhone): string {
    if (!phone?.phoneNumber) {
      return '';
    }
    let out = '';
    if (phone.areaCode) { out = '+' + phone.areaCode + ' '; }
    out += this.utilitiesService.formatPhone(phone.phoneNumber);
    if (phone.extention) { out += ':' + phone.extention; }
    return out;
  }


  isPlanFFS(): boolean {
    const contractData = this.contractData$.getValue();
    if (FFS_ENABLED && contractData?.baseContract?.ffs?.length) {
      return true;
    }
    return false;
  }

  /**
     * Checks if the banner should be displayed based on contract conditions.
     * Returns true if all business conditions are met, otherwise false.
     * Each section is extracted to a helper for clarity and maintainability.
     */
  showRealEstateRenewalBanner(): boolean {
    if (!this.isRenewalBannerFeatureEnabled()) return false;
    const contractData = this.contractData$.getValue();
    if (!contractData || !contractData?.baseContract || !contractData?.renewalContract) return false;
    const base = contractData.baseContract;
    const renewal = contractData.renewalContract;
    if (!this.isContractClientEligible(base)) return false;
    if (!this.isContractStatusEligible(base)) return false;
    if (!this.isExpirationDateValid(renewal.expirationDate)) return false;
    // If isNotRenewable flag Y then we are not suppose to show the banner if isNotRenewable N then we need to show banner.
    if ('isNotRenewable' in base && this.isNotRenewableValid(base.isNotRenewable)) return false;
    return this.isWithinExpirationWindow(renewal.expirationDate);
  }

  // --- Helper functions for showRealEstateRenewalBanner ---

  private isRenewalBannerFeatureEnabled(): boolean {
    return RE_RENEWAL_BANNER_ENABLED && !!this.contractData$.getValue()?.baseContract;
  }

  private isContractClientEligible(base: any): boolean {
    // Only HMS_CLIENT, not PURCHASE_TYPE_CODE_SHW, not autoRenew, etc.
    return (
      base.clientId === HMS_CLIENT &&
      base.purchaseTypeCode !== PURCHASE_TYPE_CODE_SHW &&
      base.autoRenew !== 'Y'
    );
  }

  private isContractStatusEligible(base: any): boolean {
    // Only ACTIVE contracts
    return base.contractStatus === CONTRACT_STATUS_UPPER_ACTIVE;
  }

  private isExpirationDateValid(expirationDate: any): boolean {
    const expirationDateUnix = Number(this.utilitiesService.getUnixDateFormYYYYmmdd(expirationDate));
    return !isNaN(expirationDateUnix) && expirationDateUnix > 0;
  }

  private isWithinExpirationWindow(expirationDate: any): boolean {
    const expirationDateUnix = Number(this.utilitiesService.getUnixDateFormYYYYmmdd(expirationDate));
    const daysToExpire = Math.floor((expirationDateUnix - Date.now()) / (1000 * 60 * 60 * 24));
    return daysToExpire >= 0 && daysToExpire < 90;
  }

  private isNotRenewableValid(isNotRenewable: string): boolean {
    return isNotRenewable === 'Y'
  }



  /**
   * Builds the renewal link using contractNumber and zipCode from contractData$.
   * Returns null if data is missing.
   */
  getDynamicRenewalLink(): string | null {
    const contractData = this.contractData$.getValue();
    const contractNumber = contractData?.baseContract?.contractNumber;
    const zipCode = contractData?.baseContract?.propertyAddress?.zip;
    const baseUrl = environment.realEstateRenewalBaseUrl;
    if (contractNumber && zipCode) {
      return `${environment.realEstateRenewalBaseUrl}/renewal/?contractNumber=${contractNumber}&zipCode=${zipCode}&utm_source=mya&utm_medium=internal&utm_campaign=renewal`;
    }
    return null;
  }

  /**
   * Returns the renewal billing alert with a dynamic link.
   */
  getRenewalBillingAlert(pageSource: string = PAGE_SOURCE.DASHBOARD, target: string = ''): IAlertCardContent {
    const link = this.getDynamicRenewalLink() || '';
    return {
      tagValue: 'Renewal action needed',
      title: `Your plan will expire soon. Renew now to stay protected and prevent lapse in coverage.<br /><br />Limited-time renewal offers may be available.`,
      button: {
        name: 'Renew your plan',
        target,
        link,
        isExternal: true
      },
      pageSource
    };
  }
}



